import { ApiErrorResponse, AuthError, AuthResult, ClerkErrorResponse, ClientApiError, ClientApiResult } from '@/types'
import * as Sentry from '@sentry/nextjs'
import { useQuery } from '@tanstack/react-query'
import {
  ApiError,
  ValidationError,
  AuthenticationError,
  AuthorizationError,
  NotFoundError,
  ConflictError,
  RateLimitError,
  ExternalServiceError,
} from './api-errors'
import { staticCacheConfig } from '@repo/api/utils'

// Type for Sentry capture context based on usage patterns
interface SentryCaptureContext {
  tags?: Record<string, string | boolean | number>
  extra?: Record<string, any>
  contexts?: Record<string, any>
}

// Create a mapping from error codes to their corresponding API error classes
const ERROR_CODE_MAP = {
  VALIDATION_ERROR: ValidationError,
  AUTH_REQUIRED: AuthenticationError,
  FORBIDDEN: AuthorizationError,
  NOT_FOUND: NotFoundError,
  CONFLICT: ConflictError,
  RATE_LIMIT_EXCEEDED: RateLimitError,
  EXTERNAL_SERVICE_ERROR: ExternalServiceError,
} as const

// Helper function to create a standardized error from API error response
function createApiErrorFromResponse(apiError: { code: string; message: string; details?: any }): ApiError {
  const { code, message, details } = apiError

  // Handle special cases that need custom parameters
  switch (code) {
    case 'NOT_FOUND':
      const resource = details?.resource || 'Resource'
      const id = details?.id
      return new NotFoundError(resource, id)
    case 'EXTERNAL_SERVICE_ERROR':
      const service = details?.service || 'Unknown service'
      return new ExternalServiceError(service, details?.originalError)
    default:
      // Use the error class directly for standard cases
      const ErrorClass = ERROR_CODE_MAP[code as keyof typeof ERROR_CODE_MAP]
      if (ErrorClass) {
        return new ErrorClass(message, details)
      }
  }

  // Fallback to generic ApiError
  return new ApiError(500, message, code, details)
}

function isApiErrorResponse(error: unknown): error is ApiErrorResponse {
  return (
    error !== null &&
    typeof error === 'object' &&
    'error' in error &&
    typeof (error as any).error === 'object' &&
    'code' in (error as any).error &&
    'message' in (error as any).error
  )
}

function isClerkError(error: unknown): error is ClerkErrorResponse {
  return (
    error !== null &&
    typeof error === 'object' &&
    'errors' in error &&
    Array.isArray((error as ClerkErrorResponse).errors) &&
    (error as ClerkErrorResponse).errors.length > 0
  )
}

function shouldCaptureInSentry(error: unknown): boolean {
  // Don't capture user-facing errors that create noise
  if (error instanceof Error) {
    const userFacingErrors = ['AbortError', 'timeout', 'fetch', 'Network error']
    if (userFacingErrors.some((err) => error.name === err || error.message.includes(err))) {
      return false
    }
  }

  // Don't capture client-side API errors (4xx) - these are usually user issues, not bugs
  if (isApiErrorResponse(error)) {
    const clientErrorCodes = [
      'VALIDATION_ERROR', // 400
      'AUTH_REQUIRED', // 401
      'FORBIDDEN', // 403
      'NOT_FOUND', // 404
      'CONFLICT', // 409
      'RATE_LIMIT_EXCEEDED', // 429
    ]

    return !clientErrorCodes.includes(error.error.code)
  }

  return true
}

/**
 * Converts any error into a standardized ClientApiError format
 * Handles API errors, Clerk errors, network errors, and unknown errors
 * TODO: clean up handling
 */
export const getClientApiErrorMessage = (error: unknown, context?: SentryCaptureContext): ClientApiError => {
  if (shouldCaptureInSentry(error)) {
    Sentry.captureException(error, {
      tags: { section: 'client-api', ...context?.tags },
      extra: {
        errorType: typeof error,
        isApiError: isApiErrorResponse(error),
        isClerkError: isClerkError(error),
        ...context?.extra,
      },
      contexts: context?.contexts,
    })
  }

  // Handle Clerk error objects (for auth operations)
  if (isClerkError(error)) {
    const firstError = error.errors[0]
    const code = firstError.code || 'UNKNOWN_CLERK_ERROR'

    let message = firstError.longMessage || firstError.shortMessage || 'An error occurred'
    if (code === 'form_code_incorrect') message = 'Verification failed. Please try again.'

    const isAccountNotFound = code === 'form_identifier_not_found'
    const retryableCodes = new Set(['internal_clerk_error', 'verification_status_unknown'])
    const isRetryable = retryableCodes.has(code)

    const redirectMap: Record<string, string> = {
      session_exists: '/',
    }

    return {
      code: code,
      message: message,
      retryable: isRetryable,
      ...(isAccountNotFound && { shouldSuggestSignUp: true }),
      ...(redirectMap[code] && { redirectTo: redirectMap[code] }),
    }
  }

  // Handle API error responses from server
  if (isApiErrorResponse(error)) {
    const apiError = error.error
    const standardizedError = createApiErrorFromResponse(apiError)

    return {
      code: standardizedError.code,
      message: standardizedError.message,
      retryable: isRetryableError(standardizedError.code, standardizedError.statusCode),
      statusCode: standardizedError.statusCode,
      details: standardizedError.details,
    }
  }

  // Handle AbortError (request cancelled)
  if (error instanceof Error && error.name === 'AbortError') {
    return {
      code: 'ABORT_ERROR',
      message: 'Request was cancelled',
      retryable: false,
    }
  }

  // Handle fetch/network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Network error - please check your connection',
      retryable: true,
    }
  }

  // Handle timeout errors
  if (error instanceof Error && error.message.includes('timeout')) {
    return {
      code: 'TIMEOUT_ERROR',
      message: 'Request timed out - please try again',
      retryable: true,
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      code: 'CLIENT_ERROR',
      message: error.message,
      retryable: true,
    }
  }

  // Fallback for unknown errors
  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
    retryable: false,
  }
}

/**
 * Auth-specific error handler for backward compatibility
 * Converts any error into AuthError format
 */
export const getAuthErrorMessage = (
  error: unknown
): AuthError & { shouldSuggestSignUp?: boolean; redirectTo?: string } => {
  const clientError = getClientApiErrorMessage(error)
  return {
    code: clientError.code,
    message: clientError.message,
    retryable: clientError.retryable,
    ...(clientError.shouldSuggestSignUp && { shouldSuggestSignUp: true }),
    ...(clientError.redirectTo && { redirectTo: clientError.redirectTo }),
  }
}

// Helper function to determine if an error is retryable
function isRetryableError(code: string, statusCode?: number): boolean {
  // Retry on server errors (5xx) and rate limits
  if (statusCode && (statusCode >= 500 || statusCode === 429)) {
    return true
  }

  // Retry on specific error codes that are known to be retryable
  // These align with the API error classes that are typically retryable
  const retryableCodes = ['EXTERNAL_SERVICE_ERROR', 'RATE_LIMIT_EXCEEDED', 'NETWORK_ERROR', 'TIMEOUT_ERROR']

  return retryableCodes.includes(code)
}

// Base configuration for React Query operations
const baseQueryConfig = {
  retryDelay: (attemptIndex: number) => Math.min(1000 * Math.pow(2, attemptIndex), 30000),
  retry: (failureCount: number, error: unknown) => {
    const clientError = getClientApiErrorMessage(error)
    return clientError.retryable && failureCount < 3
  },
  ...staticCacheConfig,
}

const baseMutationConfig = {
  retryDelay: (attemptIndex: number) => Math.min(1000 * Math.pow(2, attemptIndex), 30000),
  retry: (failureCount: number, error: unknown) => {
    const clientError = getClientApiErrorMessage(error)
    return clientError.retryable && failureCount < 3
  },
}

/**
 * Error handler for mutations (queries don't support onError in newer TanStack Query)
 * Logs the error and captures it in Sentry
 */
export const handleMutationError = (error: unknown, operation: string) => {
  const clientError = getClientApiErrorMessage(error)
  console.error(`${operation} error:`, clientError.message)
}

/**
 * Creates a standardized API handler that wraps mutations with error handling
 * Returns a function that always resolves with a ClientApiResult
 */
export const createClientApiHandler = <T extends unknown[]>(
  mutation: { mutateAsync: (...args: T) => Promise<unknown> },
  successResult: Partial<ClientApiResult> = { success: true }
) => {
  return async (...args: T): Promise<ClientApiResult> => {
    try {
      const data = await mutation.mutateAsync(...args)
      return { success: true, data, ...successResult }
    } catch (error) {
      const clientError = getClientApiErrorMessage(error)
      return { success: false, error: clientError.message }
    }
  }
}

/**
 * Creates a standardized auth handler that wraps mutations with error handling
 * Returns a function that always resolves with an AuthResult (for backward compatibility)
 */
export const createAuthHandler = <T extends unknown[]>(
  mutation: { mutateAsync: (...args: T) => Promise<unknown> },
  successResult: Partial<AuthResult> = { success: true }
) => {
  return async (...args: T): Promise<AuthResult> => {
    try {
      const data = await mutation.mutateAsync(...args)
      return { success: true, data, ...successResult }
    } catch (error) {
      const authError = getAuthErrorMessage(error)

      // Handle redirect for specific error cases
      // TODO: temp fix
      if (authError.redirectTo) {
        if (typeof window !== 'undefined') window.location.href = authError.redirectTo

        return {
          success: false,
          error: `${authError.message}, Redirecting...`,
        }
      }

      return {
        success: false,
        error: authError.message,
        ...(authError.shouldSuggestSignUp && { shouldSwitchToSignUp: true }),
      }
    }
  }
}

/**
 * Creates a standardized query hook with error handling
 * Returns a query result with standardized error format
 */
export const useQueryWithErrorHandling = <TData = unknown, TError = unknown>(
  queryConfig: Parameters<typeof useQuery<TData, TError>>[0]
) => {
  const queryResult = useQuery({
    ...baseQueryConfig,
    ...queryConfig,
  })

  return {
    ...queryResult,
    error: queryResult.error ? getClientApiErrorMessage(queryResult.error) : null,
  }
}

export { baseQueryConfig, baseMutationConfig }
